/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  Action, ModalSize, Portal, useModal, Text, ConfirmModal, useNotifications,
  HStack,
  Stack,
  ICON_CAMERA_OUTLINE,
  Spinner,
  SpinnerSize,
  Message,
  Icon,
  ICON_CHECK,
  Popover,
  SimpleTable,
  FontWeight,
  ICON_CUSTOM_ALERT,
} from '@doveit/bricks';
import { pluralizeWord } from '@doveit/hammer';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import ShootingPhotoSelection from '../shooting-photo-selection/ShootingPhotoSelection';
import { usePropertyAvailableAssets } from '../../hooks/use-property-available-assets/usePropertyAvailableAssets';
import { ContentType } from '../../../domain/types';

import { updateContentsSelection } from '../../../providers/api/content/contentProvider';
import { Content } from '../../../providers/api/dtos';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import * as styles from './SelectPhotosByShootingAction.style';

interface ChildFnProps {
  selectPhotos: VoidFunction;
}

export interface SelectPhotosByShootingActionProps {
  propertyId: number;
  children?: (props: ChildFnProps) => React.ReactNode;
  onSuccess?: (payload: Content[]) => void;
}

const ChangedPhotosSummary: React.FC<{ changedPhotos: { id: number, message: string }[] }> = ({ changedPhotos }) => (
  <Stack gap={100}>
    <Text.Body fontWeight={FontWeight.MEDIUM}>Riepilogo modifiche</Text.Body>
    <SimpleTable>
      <SimpleTable.Body>
        {changedPhotos.map((p) => (
          <SimpleTable.Row key={p.id}>
            <SimpleTable.Cell>
              <Text.BodySmall>Foto {p.id}</Text.BodySmall>
            </SimpleTable.Cell>
            <SimpleTable.Cell>
              <Text.BodySmall>{p.message}</Text.BodySmall>
            </SimpleTable.Cell>
          </SimpleTable.Row>
        ))}
      </SimpleTable.Body>
    </SimpleTable>
  </Stack>
);

const SelectPhotosByShootingAction: React.FC<SelectPhotosByShootingActionProps> = ({
  propertyId,
  onSuccess,
  children,
}) => {
  const { data: availableAssets, isLoading, mutate } = usePropertyAvailableAssets(propertyId, { contentType: [ContentType.FOTO] });

  const photos = React.useMemo(
    () => (availableAssets ?? []).filter((asset) => !!asset.shootingId),
    [availableAssets],
  );

  const initialSelectedPhotoNames = React.useMemo(
    () => photos.filter((photo) => photo.selected).map((photo) => photo.fileName),
    [photos],
  );

  const isMobile = useIsDevice('mobile');
  const selectPhotosModal = useModal();
  const { addSuccess, addError } = useNotifications();

  const [savedNames, setSavedNames] = React.useState<string[]>([]);
  const [selectedNames, setSelectedNames] = React.useState<string[]>([]);
  const [modifiedAbouts, setModifiedAbouts] = React.useState<Record<string, string | undefined>>({});
  const [isSaving, setIsSaving] = React.useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = React.useState(false);

  React.useEffect(() => {
    setSavedNames(initialSelectedPhotoNames);
    setSelectedNames(initialSelectedPhotoNames);
    setModifiedAbouts({});
  }, [initialSelectedPhotoNames, selectPhotosModal.isOpen]);

  const changedPhotos = React.useMemo(() => photos.map((photo, index) => {
    const isPreviouslySelected = savedNames.includes(photo.fileName);
    const isCurrentlySelected = selectedNames.includes(photo.fileName);

    const selectionChanged = isPreviouslySelected !== isCurrentlySelected;
    const aboutChanged = photo.fileName in modifiedAbouts;

    if (!selectionChanged && !aboutChanged) return null;

    let selectionMessage = '';
    if (selectionChanged) {
      selectionMessage = isCurrentlySelected ? 'Selezione' : 'Deselezione';
    }

    let aboutMessage = '';
    if (aboutChanged) {
      const newAbout = modifiedAbouts[photo.fileName];
      if (!photo.about && newAbout) {
        aboutMessage = 'Aggiunta descrizione';
      } else if (photo.about && !newAbout) {
        aboutMessage = 'Rimozione descrizione';
      } else {
        aboutMessage = 'Cambio descrizione';
      }
    }

    const message = [selectionMessage, aboutMessage].filter(Boolean).join(' + ');

    return {
      id: index + 1,
      message,
    };
  }).filter(Boolean) as { id: number, message: string }[], [photos, savedNames, selectedNames, modifiedAbouts]);

  const hasUnsavedChanges = changedPhotos.length > 0;

  const toggleSelection = React.useCallback((fileName: string) => {
    setSelectedNames((prev) => (prev.includes(fileName) ? prev.filter((name) => name !== fileName) : [...prev, fileName]));
  }, []);

  const selectMultiple = React.useCallback((fileNames: string[]) => {
    setSelectedNames((prev) => Array.from(new Set([...prev, ...fileNames])));
  }, []);

  const deselectMultiple = React.useCallback((fileNames: string[]) => {
    setSelectedNames((prev) => prev.filter((name) => !fileNames.includes(name)));
  }, []);

  const handleUpdateAbout = React.useCallback((fileName: string, about?: string) => {
    setModifiedAbouts((prev) => {
      const photo = photos.find((p) => p.fileName === fileName);
      if (photo?.about === about) {
        const next = { ...prev };
        delete next[fileName];
        return next;
      }
      return { ...prev, [fileName]: about };
    });
  }, [photos]);

  const handleCancelClick = () => {
    if (hasUnsavedChanges) {
      setIsCancelModalOpen(true);
    } else {
      selectPhotosModal.close();
    }
  };

  const handleConfirmCancel = () => {
    setIsCancelModalOpen(false);
    selectPhotosModal.close();
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const changedPhotoNames = new Set([
        ...selectedNames.filter((name) => !savedNames.includes(name)),
        ...savedNames.filter((name) => !selectedNames.includes(name)),
        ...Object.keys(modifiedAbouts),
      ]);

      const payload: Content[] = Array.from(changedPhotoNames).map((name) => {
        const photo = photos.find((p) => p.fileName === name)!;
        return {
          ...photo,
          selected: selectedNames.includes(name),
          about: name in modifiedAbouts ? modifiedAbouts[name] : photo.about,
        };
      });

      await updateContentsSelection(payload);
      onSuccess?.(payload);
      await mutate();
      setSavedNames(selectedNames);
      setModifiedAbouts({});
      addSuccess('Salvataggio completato con successo.');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      addError('Errore durante il salvataggio.');
    } finally {
      setIsSaving(false);
    }
  };

  const selectPhotos = React.useCallback(() => {
    setSavedNames(initialSelectedPhotoNames);
    setSelectedNames(initialSelectedPhotoNames);
    setModifiedAbouts({});
    selectPhotosModal.open();
  }, [selectPhotosModal, initialSelectedPhotoNames]);

  const selectedCounter = selectedNames.length > 0 && (
    <HStack gap={50} alignItems="center">
      <Icon path={ICON_CHECK} size={16} />
      <Text.Body as="span">
        <strong>{selectedNames.length}</strong> {pluralizeWord('selezionat', 'a', 'e', selectedNames.length)}
      </Text.Body>
    </HStack>
  );

  const unsavedChangesLabel = (
    <HStack gap={50} alignItems="center">
      <div css={styles.warningIcon}>
        <Icon path={ICON_CUSTOM_ALERT} size={16} />
      </div>
      <Text.Caption
        as="span"
        fontWeight={FontWeight.MEDIUM}
        color="warning.default.low"
      >
        Modifiche non salvate
      </Text.Caption>
    </HStack>
  );

  const cancelAction = (
    <Action
      color="neutral"
      onClick={handleCancelClick}
      disabled={isSaving}
      label="Annulla"
      width={isMobile ? '100%' : undefined}
    />
  );

  const saveAction = (
    <Action
      onClick={handleSave}
      loading={isSaving}
      color="primary"
      emphasis="high"
      disabled={!hasUnsavedChanges}
      label={isMobile ? 'Salva' : 'Salva modifiche'}
      expanded={isMobile}
    />
  );

  // On mobile there is no hover: the summary of the changes is shown by the cancel confirmation
  const mobileFooter = (
    <div css={styles.mobileFooter}>
      {(selectedNames.length > 0 || hasUnsavedChanges) && (
        <div css={styles.mobileFooterSummary}>
          {selectedCounter || <span />}
          {hasUnsavedChanges && unsavedChangesLabel}
        </div>
      )}
      <div css={styles.mobileFooterActions}>
        {cancelAction}
        {saveAction}
      </div>
    </div>
  );

  const desktopFooter = (
    <HStack gap={300}>
      {(selectedNames.length > 0 || hasUnsavedChanges) && (
        <Stack gap={0}>
          {selectedCounter}
          {hasUnsavedChanges && (
            <Popover
              placement="top-start"
              hasArrow
              // eslint-disable-next-line react/no-unstable-nested-components
              trigger={() => (
                <span css={styles.popoverTrigger}>
                  {unsavedChangesLabel}
                </span>
              )}
            >
              <div css={styles.popoverContent}>
                <ChangedPhotosSummary changedPhotos={changedPhotos} />
              </div>
            </Popover>
          )}
        </Stack>
      )}
      <HStack gap={150}>
        {cancelAction}
        {saveAction}
      </HStack>
    </HStack>
  );

  if (isLoading) {
    return (
      <HStack gap={100} alignItems="center">
        <Spinner size={SpinnerSize.SMALLER} />
        <Text.Body>Verifica presenza shooting</Text.Body>
      </HStack>
    );
  }

  if (photos.length === 0) {
    return (
      <Message message="Nessuna foto da shooting disponibile." />
    );
  }

  return (
    <>
      {children
        ? children({ selectPhotos })
        : (
          <Action
            label="Seleziona da shooting"
            onClick={selectPhotos}
            emphasis="high"
            iconLeft={{ path: ICON_CAMERA_OUTLINE }}
            size="M"
            aria-label="Pulsante per selezionare le foto dallo shooting"
          />
        )}
      <Portal>
        <SimpleModal
          {...selectPhotosModal}
          // Closing the modal behaves like the cancel action: unsaved changes need a confirmation
          close={isSaving ? undefined : handleCancelClick}
          title={isMobile ? 'Seleziona da shooting' : 'Seleziona foto da shooting'}
          size={ModalSize.XLARGE}
          // On mobile the selection manages its own scroll and padding: its toolbar stays visible
          // and the list scrolls right up to the footer
          disableScroll={isMobile}
          padded={!isMobile}
          aria-label="Modale per selezionare le foto da uno shooting"
          footerContentAlignment="right"
          footer={isMobile ? mobileFooter : desktopFooter}
        >
          <ShootingPhotoSelection
            propertyId={propertyId}
            photos={photos}
            selectedNames={selectedNames}
            modifiedAbouts={modifiedAbouts}
            isSaving={isSaving}
            onToggleSelection={toggleSelection}
            onSelectMultiple={selectMultiple}
            onDeselectMultiple={deselectMultiple}
            onUpdateAbout={handleUpdateAbout}
          />
        </SimpleModal>

        <ConfirmModal
          isOpen={isCancelModalOpen}
          onAbort={() => setIsCancelModalOpen(false)}
          onConfirm={handleConfirmCancel}
          title="Confermi di abbandonare la selezione?"
          variant="critical"
        >
          <Stack gap={300}>
            <Text.Body as="p">
              Se esci dalla selezione, perderai le modifiche apportate e la galleria dell&apos;immobile rimarrà invariata.
            </Text.Body>
            {changedPhotos.length > 0 && (
              <ChangedPhotosSummary changedPhotos={changedPhotos} />
            )}
          </Stack>
        </ConfirmModal>
      </Portal>
    </>
  );
};

export default SelectPhotosByShootingAction;
